import type { components, operations, paths } from '@volee/api-types'

import type { ApiClient } from './client.ts'

// Thin named wrappers over the paths `services/api` serves; every type comes from the
// generated schema. Plain named exports, so a page's test can `vi.spyOn` the endpoint.

export type Me = components['schemas']['Me']

/** Liveness. Public: no Bearer header, so it works before a session exists. */
export function getHealth(client: ApiClient): Promise<{ status: string }> {
  return client.get<{ status: string }>('/health', { authenticated: false })
}

/** The account behind the token. The canonical "is my session real" call. */
export function getMe(client: ApiClient): Promise<Me> {
  return client.get<Me>('/v1/me')
}

/** «Da oggi decidi tu» read, once (spec #278): the answer is `/v1/me` with no step left. */
export function readTheAutonomyNotice(client: ApiClient): Promise<Me> {
  return client.post<Me>('/v1/me/autonomy-notice')
}

// «Altro tutore»: the link that asks one more tutore in for a child, which only a tutore of theirs gets.

export type GuardianLinkRead = components['schemas']['GuardianLinkRead']

/** The link still open for this child, or a new one: asking again keeps one already sent alive. */
export function issueAnotherGuardianLink(
  client: ApiClient,
  personId: number,
): Promise<GuardianLinkRead> {
  return client.post<GuardianLinkRead>(`/v1/people/${String(personId)}/guardian-link`)
}

// «Usa questo telefono anche per …»: the proof that signs a tutore's phone in as the child (#302).

export type SignInGrant = components['schemas']['SignInGrant']

/** A minute-long proof for the child's own account, which only a tutore of theirs gets. */
export function grantChildSignIn(client: ApiClient, personId: number): Promise<SignInGrant> {
  return client.post<SignInGrant>(`/v1/people/${String(personId)}/sign-in-grant`)
}

export type PasswordVerificationRead = components['schemas']['PasswordVerificationRead']

/** The account's password, given again for what a tutore decides for a child (#301). Not `signIn`: that
 *  would replace the session on the phone. A wrong one is 422 `auth.password_wrong`. */
export function verifyPassword(
  client: ApiClient,
  password: string,
): Promise<PasswordVerificationRead> {
  return client.post<PasswordVerificationRead>('/v1/me/password-verification', { password })
}

// «Chi ti segue»: who held the caller's tutela until 14. The caller's own, and only they take one off.

export type FollowerRead = components['schemas']['FollowerRead']
export type FollowersRead = components['schemas']['FollowersRead']

export function listFollowers(client: ApiClient): Promise<FollowersRead> {
  return client.get<FollowersRead>('/v1/me/followers')
}

/** Answers the list as it stands after: the page holds one copy. */
export function removeFollower(client: ApiClient, followerId: number): Promise<FollowersRead> {
  return client.delete<FollowersRead>(`/v1/me/followers/${String(followerId)}`)
}

// Clubs. Reading is open to any authenticated caller, writing is staff only. No delete,
// deliberately — see the module docstring of `api/v1/clubs.py`.

export type ClubRead = components['schemas']['ClubRead']
export type ClubCreate = components['schemas']['ClubCreate']
export type ClubUpdate = components['schemas']['ClubUpdate']
export type ClubPage = components['schemas']['ClubPage']

type ListClubsQuery = NonNullable<operations['list_clubs_v1_clubs_get']['parameters']['query']>

// Derived from the response: Pydantic inlines the `Literal`, so the generated schema has
// no named type, and hand-writing the five values would let them drift.
export type ClubStatus = ClubRead['status']

/** The orderings the listing accepts. A leading minus is descending. */
export type ClubSort = NonNullable<ListClubsQuery['sort']>

// An alias and not an interface: only aliases get the implicit index signature
// `QueryParams` needs. Nothing is defaulted — `buildUrl` drops `undefined` and `null`.
export type ClubFilters = {
  readonly limit?: number
  readonly offset?: number
  /** Matches `legal_name` or `short_name`, case-insensitively. */
  readonly q?: string | null
  readonly status?: ClubStatus | null
  readonly sort?: ClubSort
}

// The 107 sigle with their name and region. Closed, national and unchanging, so a caller
// fetches it once — it exists so neither app carries the map itself.

export type ProvinceRead = components['schemas']['ProvinceRead']
export type ProvinceList = components['schemas']['ProvinceList']

// From the payload, not the response: `ClubRead.province` is a plain string, while
// `ClubUpdate` carries the Literal that names the 107 values.
export type ClubProvince = NonNullable<ClubUpdate['province']>

export function listProvinces(client: ApiClient): Promise<ProvinceList> {
  return client.get<ProvinceList>('/v1/provinces')
}

// The 250 codes a player's nationality may be, plus XK. Closed and international, so a
// caller fetches it once — names come from `Intl.DisplayNames` in the UI's own language.

export type CountryList = components['schemas']['CountryList']

/** One element of `CountryList.items`: the exact union the endpoint promises, not `string`. */
export type Nationality = CountryList['items'][number]

export function listCountries(client: ApiClient): Promise<CountryList> {
  return client.get<CountryList>('/v1/countries')
}

/** One page of clubs, plus the total of everything the filter matches. */
export function listClubs(client: ApiClient, filters: ClubFilters = {}): Promise<ClubPage> {
  return client.get<ClubPage>('/v1/clubs', { query: filters })
}

/** One club. A 404 carries `club.not_found` with the id in `params`. */
export function getClub(client: ApiClient, clubId: number): Promise<ClubRead> {
  return client.get<ClubRead>(`/v1/clubs/${String(clubId)}`)
}

export type ClubOverview = components['schemas']['ClubOverview']
/** The reader's bond with a club, the highest that holds: what its page shows follows it. */
export type ClubBond = ClubOverview['bond']

/** Any session: the reader's bond with the club and its three counts, the tesserati `null`
 *  where the bond does not reach the roster. */
export function getClubOverview(client: ApiClient, clubId: number): Promise<ClubOverview> {
  return client.get<ClubOverview>(`/v1/clubs/${String(clubId)}/overview`)
}

export type ClubOnboarding = components['schemas']['ClubOnboarding']
export type OnboardingStep = components['schemas']['OnboardingStep']
export type OnboardingStepKey = OnboardingStep['key']
export type OnboardingStepState = OnboardingStep['state']

/** I primi passi of a società, every voce read off its data, in the advised order. Its
 *  dirigenti and staff: anyone else, a coach of the club included, is refused with a 403. */
export function getClubOnboarding(client: ApiClient, clubId: number): Promise<ClubOnboarding> {
  return client.get<ClubOnboarding>(`/v1/clubs/${String(clubId)}/onboarding`)
}

/** «Lo farò dopo»: the caller's, on every device of theirs. Idempotent; answers the read. */
export function postponeClubOnboarding(client: ApiClient, clubId: number): Promise<ClubOnboarding> {
  return client.put<ClubOnboarding>(`/v1/clubs/${String(clubId)}/onboarding/postponement`)
}

/** «Riprendi»: takes off the caller's «Lo farò dopo». Idempotent; answers the read. */
export function resumeClubOnboarding(client: ApiClient, clubId: number): Promise<ClubOnboarding> {
  return client.delete<ClubOnboarding>(`/v1/clubs/${String(clubId)}/onboarding/postponement`)
}

/** «Sono queste»: a dirigente says the squads Volée entered are the società's, for all of them and for
 *  good. Idempotent; answers the read. A società with no squad is a 422 `error.unprocessable`. */
export function confirmClubTeams(client: ApiClient, clubId: number): Promise<ClubOnboarding> {
  return client.put<ClubOnboarding>(`/v1/clubs/${String(clubId)}/onboarding/teams-confirmation`)
}

/** «Chiudi», for the whole società and for good. A voce that counts still to do is a 422
 *  `onboarding.not_done`; closing again changes nothing. */
export function closeClubOnboarding(client: ApiClient, clubId: number): Promise<ClubOnboarding> {
  return client.put<ClubOnboarding>(`/v1/clubs/${String(clubId)}/onboarding/closure`)
}

export type UnassignedPlayerList = components['schemas']['UnassignedPlayerList']
export type UnassignedPlayer = components['schemas']['UnassignedPlayer']
/** Why the annata proposes no squad: two or more, none of the tesserato's sex, none for the year. */
export type NoProposal = NonNullable<UnassignedPlayer['no_proposal']>
export type TeamAssignment = components['schemas']['TeamAssignment']

/** «Le leve»: the senza squadra of the season, each with the squad its annata proposes or why it
 *  proposes none. A club's dirigenti and staff: the staff of a squad is refused, even in a seat. */
export function listUnassignedPlayers(
  client: ApiClient,
  clubId: number,
): Promise<UnassignedPlayerList> {
  return client.get<UnassignedPlayerList>(`/v1/clubs/${String(clubId)}/unassigned-players`)
}

/** Puts senza squadra in their rose, all in one act: a refusal of any writes none, and one
 *  already in a rosa is a 409 `registration.already_in_a_team`, never moved. */
export function assignTeams(
  client: ApiClient,
  clubId: number,
  assignments: readonly TeamAssignment[],
): Promise<void> {
  return client.post<void>(`/v1/clubs/${String(clubId)}/team-assignments`, {
    assignments: [...assignments],
  })
}

// Staff only. The response carries the database-generated `id`, which is the only way the
// caller learns it. A duplicate `legal_name` is a 409 `club.legal_name_taken`.
export function createClub(client: ApiClient, body: ClubCreate): Promise<ClubRead> {
  return client.post<ClubRead>('/v1/clubs', body)
}

// Staff only. Send only the changed fields: the backend applies `exclude_unset`, so an
// omitted field is left alone and an explicit `null` clears a nullable one.
export function updateClub(client: ApiClient, clubId: number, body: ClubUpdate): Promise<ClubRead> {
  return client.patch<ClubRead>(`/v1/clubs/${String(clubId)}`, body)
}

/** The formats every image upload takes, written as a file picker's `accept`. It mirrors
 *  `CONTENT_TYPES` in services/api `core/images.py`, and the server re-checks the bytes. */
export const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp'

/** The most an uploaded image may weigh: `MAX_IMAGE_BYTES` in services/api `core/images.py`,
 *  which refuses more with the upload's `*_too_large`. Weighed here, a file is refused before
 *  the request leaves. */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024

// Staff only. PUT because the last file wins, so a retry means what the first attempt did.
// A refused file answers 422 `club.logo_invalid_type` or `club.logo_too_large`.
export function uploadClubLogo(client: ApiClient, clubId: number, file: File): Promise<ClubRead> {
  const body = new FormData()
  body.append('file', file)
  return client.put<ClubRead>(`/v1/clubs/${String(clubId)}/logo`, body)
}

// A società's two kits. Any session reads them; staff and its dirigenti di società write.

export type ClubKits = components['schemas']['ClubKits']
export type KitRead = components['schemas']['KitRead']
export type KitPut = components['schemas']['KitPut']
export type KitRank = KitRead['rank']
export type KitDesign = KitRead['design']

/** Both ranks, each `null` while the società has not declared it. */
export function getClubKits(client: ApiClient, clubId: number): Promise<ClubKits> {
  return client.get<ClubKits>(`/v1/clubs/${String(clubId)}/kits`)
}

/** Creates or replaces one kit. The second answers 409 `kit.second_needs_first` until
 *  there is a first; the colours come back upper-case. */
export function putClubKit(
  client: ApiClient,
  clubId: number,
  rank: KitRank,
  body: KitPut,
): Promise<KitRead> {
  return client.put<KitRead>(`/v1/clubs/${String(clubId)}/kits/${rank}`, body)
}

/** The first answers 409 `kit.first_needed_by_second` while there is a second. */
export function deleteClubKit(client: ApiClient, clubId: number, rank: KitRank): Promise<void> {
  return client.delete<void>(`/v1/clubs/${String(clubId)}/kits/${rank}`)
}

// A squad's two kits, besides its società's: the same shape and rules, so the same `KitRead` and
// `KitPut`. Any session reads them; whoever composes the squad's rosa writes.

export type TeamKits = components['schemas']['TeamKits']

/** Both ranks, each `null` while the squad has not declared it. */
export function getTeamKits(client: ApiClient, teamId: number): Promise<TeamKits> {
  return client.get<TeamKits>(`/v1/teams/${String(teamId)}/kits`)
}

/** Creates or replaces one kit, with the società's 409 `kit.second_needs_first` and
 *  colours upper-cased on the way back. */
export function putTeamKit(
  client: ApiClient,
  teamId: number,
  rank: KitRank,
  body: KitPut,
): Promise<KitRead> {
  return client.put<KitRead>(`/v1/teams/${String(teamId)}/kits/${rank}`, body)
}

/** The first answers 409 `kit.first_needed_by_second` while there is a second. */
export function deleteTeamKit(client: ApiClient, teamId: number, rank: KitRank): Promise<void> {
  return client.delete<void>(`/v1/teams/${String(teamId)}/kits/${rank}`)
}

// Staff only. A club that never had one answers 404 `club.logo_missing`: "there is none"
// and "removed it" are different answers.
export function deleteClubLogo(client: ApiClient, clubId: number): Promise<void> {
  return client.delete<void>(`/v1/clubs/${String(clubId)}/logo`)
}

// The logo's twin, for the photo the club's hero opens on: the same guard, the same 2 MB, and
// 422 `club.cover_invalid_type` or `club.cover_too_large` for a refused file.
export function uploadClubCover(client: ApiClient, clubId: number, file: File): Promise<ClubRead> {
  const body = new FormData()
  body.append('file', file)
  return client.put<ClubRead>(`/v1/clubs/${String(clubId)}/cover`, body)
}

// A club with no cover answers 404 `club.cover_missing`, as the logo does.
export function deleteClubCover(client: ApiClient, clubId: number): Promise<void> {
  return client.delete<void>(`/v1/clubs/${String(clubId)}/cover`)
}

// Pitches — the single playing surface, not the facility: campo A and campo B of one
// impianto are two of these. Staff writes, anyone reads, and there is no delete.

export type PitchRead = components['schemas']['PitchRead']
export type PitchCreate = components['schemas']['PitchCreate']
export type PitchUpdate = components['schemas']['PitchUpdate']
export type PitchPage = components['schemas']['PitchPage']

type ListPitchesQuery = NonNullable<
  operations['list_pitches_v1_pitches_get']['parameters']['query']
>

/** The orderings the listing accepts. A leading minus is descending. */
export type PitchSort = NonNullable<ListPitchesQuery['sort']>

/** A type alias and not an interface: only an alias gets the implicit index signature
 * `QueryParams` requires, so the same declaration as an `interface` does not compile. */
export type PitchFilters = {
  readonly limit?: number
  readonly offset?: number
  /** Matches the name or the address, case-insensitively. */
  readonly q?: string | null
  readonly sort?: PitchSort
}

/** One page of pitches, plus the total of everything the filter matches. */
export function listPitches(client: ApiClient, filters: PitchFilters = {}): Promise<PitchPage> {
  return client.get<PitchPage>('/v1/pitches', { query: filters })
}

/** One pitch. A 404 carries `pitch.not_found` with the id in `params`. */
export function getPitch(client: ApiClient, pitchId: number): Promise<PitchRead> {
  return client.get<PitchRead>(`/v1/pitches/${String(pitchId)}`)
}

// Staff only. No 409 to bind: the name is not unique, because every province has a
// "Comunale" and refusing the second would be wrong more often than the duplicate.
export function createPitch(client: ApiClient, body: PitchCreate): Promise<PitchRead> {
  return client.post<PitchRead>('/v1/pitches', body)
}

// Staff only. Send only the changed fields — same `exclude_unset` contract as a club.
export function updatePitch(
  client: ApiClient,
  pitchId: number,
  body: PitchUpdate,
): Promise<PitchRead> {
  return client.patch<PitchRead>(`/v1/pitches/${String(pitchId)}`, body)
}

// Sources of comunicati — the pages the back office fetches them from, each switched on or off.

export type BulletinSourceRead = components['schemas']['BulletinSourceRead']
export type BulletinSourceCreate = components['schemas']['BulletinSourceCreate']
export type BulletinSourceUpdate = components['schemas']['BulletinSourceUpdate']
export type BulletinSourceList = components['schemas']['BulletinSourceList']
export type BulletinCheckRead = components['schemas']['BulletinCheckRead']
export type BulletinSchedule = components['schemas']['BulletinSchedule']
export type CheckIssue = components['schemas']['CheckIssue']
export type IgnoreIssue = components['schemas']['IgnoreIssue']
export type IgnoredIssue = components['schemas']['IgnoredIssue']
export type AliasRead = components['schemas']['AliasRead']

/** GET /v1/bulletin-sources — staff only, in name order. */
export function listBulletinSources(client: ApiClient): Promise<BulletinSourceList> {
  return client.get<BulletinSourceList>('/v1/bulletin-sources')
}

/** POST /v1/bulletin-sources — born switched off. */
export function createBulletinSource(
  client: ApiClient,
  body: BulletinSourceCreate,
): Promise<BulletinSourceRead> {
  return client.post<BulletinSourceRead>('/v1/bulletin-sources', body)
}

/** PATCH /v1/bulletin-sources/{id} — rename, repoint, or switch on and off. */
export function updateBulletinSource(
  client: ApiClient,
  sourceId: number,
  body: BulletinSourceUpdate,
): Promise<BulletinSourceRead> {
  return client.patch<BulletinSourceRead>(`/v1/bulletin-sources/${String(sourceId)}`, body)
}

/** POST /v1/bulletin-sources/{id}/ignored — «Da sistemare» stops listing the item, for good or for
 *  this check alone; it counts at once. */
export function ignoreBulletinIssue(
  client: ApiClient,
  sourceId: number,
  body: IgnoreIssue,
): Promise<void> {
  return client.post<void>(`/v1/bulletin-sources/${String(sourceId)}/ignored`, body)
}

/** DELETE /v1/bulletin-sources/{id}/ignored/{fingerprint} — the next check lists the item again. */
export function restoreBulletinIssue(
  client: ApiClient,
  sourceId: number,
  fingerprint: string,
): Promise<void> {
  return client.delete<void>(
    `/v1/bulletin-sources/${String(sourceId)}/ignored/${encodeURIComponent(fingerprint)}`,
  )
}

/** POST /v1/bulletin-sources/{id}/rereads — the next check reads that part of the document again. */
export function rereadBulletinPart(
  client: ApiClient,
  sourceId: number,
  document: string,
  key: string,
): Promise<void> {
  return client.post<void>(`/v1/bulletin-sources/${String(sourceId)}/rereads`, { document, key })
}

/** POST /v1/clubs/{id}/aliases — another name the comunicati give a club; 409 `club.alias_taken`. */
export function createClubAlias(
  client: ApiClient,
  clubId: number,
  name: string,
): Promise<AliasRead> {
  return client.post<AliasRead>(`/v1/clubs/${String(clubId)}/aliases`, { name })
}

/** POST /v1/pitches/{id}/aliases — another name the comunicati give a ground; 409 `pitch.alias_taken`. */
export function createPitchAlias(
  client: ApiClient,
  pitchId: number,
  name: string,
): Promise<AliasRead> {
  return client.post<AliasRead>(`/v1/pitches/${String(pitchId)}/aliases`, { name })
}

/** POST /v1/bulletin-sources/{id}/checks — «Controlla ora»: answers at once with the running check. */
export function startBulletinSourceCheck(
  client: ApiClient,
  sourceId: number,
): Promise<BulletinCheckRead> {
  return client.post<BulletinCheckRead>(`/v1/bulletin-sources/${String(sourceId)}/checks`)
}

export type PitchClubRead = components['schemas']['PitchClubRead']
export type PitchClubList = components['schemas']['PitchClubList']

/** The clubs that play on one pitch — the link read the other way round. Unpaged: a campo
 * serves one club, or the two that share a comune's ground. */
export function listPitchClubs(client: ApiClient, pitchId: number): Promise<PitchClubList> {
  return client.get<PitchClubList>(`/v1/pitches/${String(pitchId)}/clubs`)
}

// A club's pitches, many-to-many: a municipal campo is shared by two clubs, so
// `is_primary` belongs to the link and is nested with it rather than flattened.

export type ClubPitchRead = components['schemas']['ClubPitchRead']
export type ClubPitchList = components['schemas']['ClubPitchList']
export type ClubPitchLink = components['schemas']['ClubPitchLink']
export type ClubPitchCreate = components['schemas']['ClubPitchCreate']

/** Every pitch the club plays on, primary first. Unpaged: a club has a handful. */
export function listClubPitches(client: ApiClient, clubId: number): Promise<ClubPitchList> {
  return client.get<ClubPitchList>(`/v1/clubs/${String(clubId)}/pitches`)
}

export type ClubCameraList = components['schemas']['ClubCameraList']
export type ClubPitchCameras = components['schemas']['ClubPitchCameras']
export type ClubGatewayRead = components['schemas']['ClubGatewayRead']
export type ClubCameraRead = components['schemas']['ClubCameraRead']

/** Whether each pitch has its camera, since when, and what its box runs. The club's
 * officials and staff only: a coach is refused here, unlike on the pitch list above. */
export function listClubCameras(client: ApiClient, clubId: number): Promise<ClubCameraList> {
  return client.get<ClubCameraList>(`/v1/clubs/${String(clubId)}/cameras`)
}

/** Staff only. Creates the pitch and the link in one transaction, so a failure cannot
 * leave a campo nobody asked for attached to nothing. */
export function createClubPitch(
  client: ApiClient,
  clubId: number,
  body: ClubPitchCreate,
): Promise<ClubPitchRead> {
  return client.post<ClubPitchRead>(`/v1/clubs/${String(clubId)}/pitches`, body)
}

// Staff only, and a PUT: attaching the same pitch twice means what once means. Omit
// `is_primary` to leave the current setting alone; `true` demotes the club's other one.
export function attachPitch(
  client: ApiClient,
  clubId: number,
  pitchId: number,
  body: ClubPitchLink = {},
): Promise<ClubPitchRead> {
  return client.put<ClubPitchRead>(`/v1/clubs/${String(clubId)}/pitches/${String(pitchId)}`, body)
}

// Staff only. Removes the link and nothing else: the pitch and its gateway stay. A club
// that does not play there answers 404 `club.pitch_not_linked`.
export function detachPitch(client: ApiClient, clubId: number, pitchId: number): Promise<void> {
  return client.delete<void>(`/v1/clubs/${String(clubId)}/pitches/${String(pitchId)}`)
}

// The sides a club fields. Read by any authenticated caller, written by staff — and
// unlike a club, a team can be deleted: it is a row an operator adds and can get wrong.

export type TeamRead = components['schemas']['TeamRead']
/** One team read by its own id: `TeamRead` and the signed URL of its cover. A list does not carry it. */
export type TeamDetailRead = components['schemas']['TeamDetailRead']
export type TeamCreate = components['schemas']['TeamCreate']
export type TeamUpdate = components['schemas']['TeamUpdate']
/** A team as a club's list has it: `TeamRead`, and whether it keeps kits of its own. */
export type TeamListItem = components['schemas']['TeamListItem']
export type TeamList = components['schemas']['TeamList']

/** A team named from somewhere else — a tesseramento's list of squadre — without the
 * timestamps `TeamRead` carries. */
export type TeamBrief = components['schemas']['TeamBrief']

/** Derived from the response, as `ClubStatus` is: Pydantic inlines the `Literal`. */
export type AgeGroup = TeamRead['age_group']
export type TeamGender = TeamRead['gender']

/** Every team of the club, oldest category first. Unpaged: a club fields a handful. */
export function listClubTeams(client: ApiClient, clubId: number): Promise<TeamList> {
  return client.get<TeamList>(`/v1/clubs/${String(clubId)}/teams`)
}

/** Staff only. A duplicate answers 409 `team.already_exists`. */
export function createClubTeam(
  client: ApiClient,
  clubId: number,
  body: TeamCreate,
): Promise<TeamRead> {
  return client.post<TeamRead>(`/v1/clubs/${String(clubId)}/teams`, body)
}

/** Staff only. Send only the fields that changed. */
export function updateTeam(client: ApiClient, teamId: number, body: TeamUpdate): Promise<TeamRead> {
  return client.patch<TeamRead>(`/v1/teams/${String(teamId)}`, body)
}

/** Read by any authenticated caller — the name of a side is not private. */
export function getTeam(client: ApiClient, teamId: number): Promise<TeamDetailRead> {
  return client.get<TeamDetailRead>(`/v1/teams/${String(teamId)}`)
}

// The club cover's twin, under the rosa's guard (staff, a dirigente di società, whoever holds a seat of the squad):
// 422 `team.cover_invalid_type` or `team.cover_too_large` for a refused file.
export function uploadTeamCover(
  client: ApiClient,
  teamId: number,
  file: File,
): Promise<TeamDetailRead> {
  const body = new FormData()
  body.append('file', file)
  return client.put<TeamDetailRead>(`/v1/teams/${String(teamId)}/cover`, body)
}

// A team with no cover answers 404 `team.cover_missing`, as a club does.
export function removeTeamCover(client: ApiClient, teamId: number): Promise<void> {
  return client.delete<void>(`/v1/teams/${String(teamId)}/cover`)
}

// Staff only. A team with enrollments or appointments answers 409 `team.in_use`, so no
// history ever leaves with it. Its rosa goes with it; the tesseramenti stay.
export function deleteTeam(client: ApiClient, teamId: number): Promise<void> {
  return client.delete<void>(`/v1/teams/${String(teamId)}`)
}

// La rosa. Narrower than the team it hangs off: a list of minors reaches staff, a
// dirigente di società, and whoever holds a seat of that one squad — nobody else.

export type TeamRosterList = components['schemas']['TeamRosterList']
/** A row of a squad's rosa: the tesserato and the signed URL of the photo the scheda shows. */
export type TeamRosterPlayerRead = components['schemas']['TeamRosterPlayerRead']
export type TeamCandidateList = components['schemas']['TeamCandidateList']
export type TeamCandidateRead = components['schemas']['TeamCandidateRead']
export type TeamRosterAdded = components['schemas']['TeamRosterAdded']

// Aliases and not interfaces, for the reason `PersonFilters` gives: only an alias gets
// the implicit index signature `QueryParams` needs.
export type TeamRosterFilters = {
  /** The year the season opens in: 2026 is 2026/27. Omitted means the one containing today. */
  readonly season_year?: number | null
}

export type TeamCandidateFilters = TeamRosterFilters & {
  readonly q?: string | null
  readonly position?: PlayerPosition | null
  /** Only the senza squadra — tesserati in no rosa at all — applied by the server before `limit`. */
  readonly unassigned?: boolean
  readonly limit?: number
}

/** Unpaged: a rosa is twenty-odd players. The response repeats the season it answered for. */
export function listTeamPlayers(
  client: ApiClient,
  teamId: number,
  filters: TeamRosterFilters = {},
): Promise<TeamRosterList> {
  return client.get<TeamRosterList>(`/v1/teams/${String(teamId)}/players`, { query: filters })
}

/** The club's tesserati this squad could take: active, and not already in it. */
export function listTeamCandidates(
  client: ApiClient,
  teamId: number,
  filters: TeamCandidateFilters = {},
): Promise<TeamCandidateList> {
  return client.get<TeamCandidateList>(`/v1/teams/${String(teamId)}/candidates`, { query: filters })
}

/** Tesseramenti and never people: a transfer is two registrations in one season, and
 *  choosing between them is the caller's. Always a list, one element included, and one
 *  transaction — a refusal writes nothing. Already in the rosa answers 409. */
export function addTeamPlayers(
  client: ApiClient,
  teamId: number,
  registrationIds: readonly number[],
): Promise<TeamRosterAdded> {
  return client.post<TeamRosterAdded>(`/v1/teams/${String(teamId)}/players`, {
    registration_ids: [...registrationIds],
  })
}

/** La leva of one tesseramento — a tesseramento and never a person, who can hold two in a
 *  season. `null` takes them out of the rosa; requires an incarico **di società**, so an
 *  allenatore reads the panel instead of using it. */
export function setRegistrationTeam(
  client: ApiClient,
  registrationId: number,
  teamId: number | null,
): Promise<PlayerRegistrationRead> {
  return client.put<PlayerRegistrationRead>(`/v1/registrations/${String(registrationId)}/team`, {
    team_id: teamId,
  })
}

// Leaving a squad is not leaving the società: the tesseramento is untouched.
export function removeTeamPlayer(
  client: ApiClient,
  teamId: number,
  registrationId: number,
): Promise<void> {
  return client.delete<void>(`/v1/teams/${String(teamId)}/players/${String(registrationId)}`)
}

// Volee staff — who can get into the back-office, and until when. Staff only, reading
// included, unlike clubs: an email address is not public.

export type StaffRead = components['schemas']['StaffRead']
export type StaffPage = components['schemas']['StaffPage']
export type StaffInvite = components['schemas']['StaffInvite']

type ListStaffQuery = NonNullable<operations['list_staff_v1_staff_get']['parameters']['query']>

/** Not `Role` from `./roles.ts`: that one is the app-level vocabulary `rolesOf` derives. */
export type StaffMemberRole = StaffRead['role']

/** The orderings the listing accepts. A leading minus is descending. */
export type StaffSort = NonNullable<ListStaffQuery['sort']>

export type StaffFilters = {
  readonly limit?: number
  readonly offset?: number
  /** Matches the email, the last name or the first name, case-insensitively. */
  readonly q?: string | null
  readonly role?: StaffMemberRole | null
  /** Tri-state: in service today, revoked or not yet started, or — omitted — both. */
  readonly active?: boolean | null
  readonly sort?: StaffSort
}

/** One page of staff, plus the total of everything the filter matches. */
export function listStaff(client: ApiClient, filters: StaffFilters = {}): Promise<StaffPage> {
  return client.get<StaffPage>('/v1/staff', { query: filters })
}

// Admin only: another staff role gets 403 `auth.admin_required`. The backend creates the
// Cognito identity and Cognito mails the temporary password — no secret comes back here.
export function createStaff(client: ApiClient, body: StaffInvite): Promise<StaffRead> {
  return client.post<StaffRead>('/v1/staff', body)
}

// Accounts as the back office reads them: plan, purchases, last sighting. Staff only.
// Spec: docs/superpowers/specs/2026-10-15-admin-utenti-design.md.

export type UserSummary = components['schemas']['UserSummary']
export type UserPage = components['schemas']['UserPage']
export type UserDetail = components['schemas']['UserDetail']
export type UserSubscription = components['schemas']['UserSubscription']
export type UserPurchase = components['schemas']['UserPurchase']

type ListUsersQuery = NonNullable<operations['list_users_v1_users_get']['parameters']['query']>

export type AccountType = UserSummary['account_type']
export type UserPlan = UserSummary['plan']
/** The orderings the listing accepts. A leading minus is descending. */
export type UserSort = NonNullable<ListUsersQuery['sort']>

export type UserFilters = {
  readonly limit?: number
  readonly offset?: number
  /** Matches the email, the last name or the first name, case-insensitively. */
  readonly q?: string | null
  readonly account_type?: AccountType | null
  readonly plan?: UserPlan | null
  readonly has_purchases?: boolean | null
  readonly sort?: UserSort
}

export function listUsers(client: ApiClient, filters: UserFilters = {}): Promise<UserPage> {
  return client.get<UserPage>('/v1/users', { query: filters })
}

export function getUser(client: ApiClient, userId: number): Promise<UserDetail> {
  return client.get<UserDetail>(`/v1/users/${String(userId)}`)
}

// The caller's own staff profile: names, phone and photo, written from the back office
// settings. The email is not here — it is the Cognito sign-in name.

export type StaffProfileRead = components['schemas']['StaffProfileRead']
export type StaffProfileUpdate = components['schemas']['StaffProfileUpdate']

export function getOwnStaffProfile(client: ApiClient): Promise<StaffProfileRead> {
  return client.get<StaffProfileRead>('/v1/staff/me')
}

// Send only the changed fields: an omitted one is left alone, an explicit `null` clears it.
export function updateOwnStaffProfile(
  client: ApiClient,
  body: StaffProfileUpdate,
): Promise<StaffProfileRead> {
  return client.patch<StaffProfileRead>('/v1/staff/me', body)
}

// The logo's twin: 422 `staff.photo_invalid_type` or `staff.photo_too_large` for a refused file.
export function uploadOwnStaffPhoto(client: ApiClient, file: File): Promise<StaffProfileRead> {
  const body = new FormData()
  body.append('file', file)
  return client.put<StaffProfileRead>('/v1/staff/me/photo', body)
}

// None to remove answers 404 `staff.photo_missing`.
export function deleteOwnStaffPhoto(client: ApiClient): Promise<void> {
  return client.delete<void>('/v1/staff/me/photo')
}

export type SecondFactorReset = components['schemas']['SecondFactorReset']

// Admin only, same as createStaff. Works on any account, not just staff — the id is
// app_user.id.
export function resetSecondFactor(client: ApiClient, userId: number): Promise<SecondFactorReset> {
  return client.delete<SecondFactorReset>(`/v1/accounts/${String(userId)}/second-factor`)
}

// Seasons — the calendar every tesseramento hangs off, derived from a 1 July boundary and
// never managed. Listing is open to any authenticated caller: season names are not personal.

export type SeasonRead = components['schemas']['SeasonRead']
export type SeasonList = components['schemas']['SeasonList']
export type SeasonUpdate = components['schemas']['SeasonUpdate']

/** Every season, newest first, and which one contains today — answered by the server so no
 *  client compares the dates in its own timezone. The one after the current season is
 *  always in the list, whether or not anybody has written into it yet. */
export function listSeasons(client: ApiClient): Promise<SeasonList> {
  return client.get<SeasonList>('/v1/seasons')
}

/** Move a boundary. Staff only, and correction only: seasons are neither created nor
 *  deleted here — the calendar makes them. Addressed by year, like everything else. */
export function updateSeason(
  client: ApiClient,
  year: number,
  body: SeasonUpdate,
): Promise<SeasonRead> {
  return client.patch<SeasonRead>(`/v1/seasons/${String(year)}`, body)
}

// Players — the anagrafica, and the tesseramenti binding them to a club for a season.
// Staff only throughout, reading included: a row is a minor's name and birth date.

export type PlayerListItem = components['schemas']['PlayerListItem']
export type PlayerPage = components['schemas']['PlayerPage']
export type PlayerUpdate = components['schemas']['PlayerUpdate']
export type PlayerRegistrationRead = components['schemas']['PlayerRegistrationRead']
export type ActiveRegistration = components['schemas']['ActiveRegistration']
export type ClubBrief = components['schemas']['ClubBrief']
export type PersonRead = components['schemas']['PersonRead']
export type PersonUpdate = components['schemas']['PersonUpdate']
export type PlayerFacet = components['schemas']['PlayerFacet']

/** How a player set their profile up. Null inside the facet for a reader from outside the
 *  società, which is how a client knows it is holding the reduced card. */
export type ProfileVisibility = components['schemas']['ProfileVisibility']

/** Derived from the response, like ClubStatus: Pydantic inlines the Literal. */
export type PlayerPosition = NonNullable<PlayerFacet['position']>

/** Derived from the response, like `PlayerPosition` above. */
export type PlayerFoot = NonNullable<PlayerFacet['preferred_foot']>

/** Anagrafica, so it comes off the person's own read projection — like `PlayerPosition`
 * above. Not off `ClubPlayerCreate`, whose required-ness is a stance that can relax. */
export type Sex = NonNullable<PersonRead['sex']>

type ListPlayersQuery = NonNullable<
  operations['list_players_v1_players_get']['parameters']['query']
>

export type PlayerSort = NonNullable<ListPlayersQuery['sort']>

export type PlayerFilters = {
  readonly limit?: number
  readonly offset?: number
  /** Matches the last name or the first name, case-insensitively. */
  readonly q?: string | null
  /** Only those tesserati with this club now. Leaves out anyone tesserato nowhere. */
  readonly club_id?: number | null
  readonly sort?: PlayerSort
}

/** One page of the anagrafica. Only people who play are in it. */
export function listPlayers(client: ApiClient, filters: PlayerFilters = {}): Promise<PlayerPage> {
  return client.get<PlayerPage>('/v1/players', { query: filters })
}

// One person, whatever they are to a club: the anagrafica, the incarichi they hold, and the
// player facet when they play. Staff only — a row here is a name and a birth date.

export type PersonPage = components['schemas']['PersonPage']
export type PersonListItem = components['schemas']['PersonListItem']

// An alias and not an interface: only aliases get the implicit index signature `QueryParams`
// needs. Nothing is defaulted — `buildUrl` drops `undefined` and `null`.
export type PersonFilters = {
  readonly limit?: number
  readonly offset?: number
  /** Matches `last_name` or `first_name`, case-insensitively. */
  readonly q?: string | null
  /** Only those who play, or only those who do not. Officials and players are two
   *  populations for now, so the organigramma's picker asks for `false`. */
  readonly plays?: boolean | null
}

/** The anagrafica of ANYONE. Wider than `listPlayers`, which only knows those who play — a
 *  presidente is not in that one, which is why the organigramma had to retype identities. */
export function listPeople(client: ApiClient, filters: PersonFilters = {}): Promise<PersonPage> {
  return client.get<PersonPage>('/v1/people', { query: filters })
}

// The requests a parent's own account made and the società has not approved. The enrolment
// flow's other reads are public and live in the app; this one answers whoever the token names.

export type FamilyEnrolmentMineRead = components['schemas']['FamilyEnrolmentMineRead']
export type FamilyEnrolmentMineList = components['schemas']['FamilyEnrolmentMineList']

/** Waiting or refused, newest first; an approved one is the child's card by now. Never another
 *  account's, and an error is never an empty list: the caller tells the two apart. */
export function listOwnFamilyEnrolmentRequests(
  client: ApiClient,
): Promise<FamilyEnrolmentMineList> {
  return client.get<FamilyEnrolmentMineList>('/v1/family-enrolment-requests/mine')
}

/** The card. `player: null` is the answer to "do they play?" — not a null `position`. */
export function getPerson(client: ApiClient, personId: number): Promise<PersonRead> {
  return client.get<PersonRead>(`/v1/people/${String(personId)}`)
}

/** The same card as read by someone outside the società: the owner's own preview. A
 *  function rather than an optional argument, because the cache key is a different one. */
export function getPublicPerson(client: ApiClient, personId: number): Promise<PersonRead> {
  return client.get<PersonRead>(`/v1/people/${String(personId)}`, { query: { as: 'public' } })
}

/** Staff only, and the ONE writer of the columns every other projection reads. Send only
 *  the fields that changed. A duplicate identity answers 409 `person.already_exists`. */
export function updatePerson(
  client: ApiClient,
  personId: number,
  body: PersonUpdate,
): Promise<PersonRead> {
  return client.patch<PersonRead>(`/v1/people/${String(personId)}`, body)
}

/** The player facet's only column. A person who has never played answers 404
 *  `player.not_found`, and the response is the whole card either way. */
export function updatePlayer(
  client: ApiClient,
  personId: number,
  body: PlayerUpdate,
): Promise<PersonRead> {
  return client.patch<PersonRead>(`/v1/players/${String(personId)}`, body)
}

// The player's own, and their guardian's: the club is refused with
// `player.field_reserved_to_the_player`. PUT because the last file wins.
export function uploadPlayerPhoto(
  client: ApiClient,
  personId: number,
  file: File,
): Promise<PersonRead> {
  const body = new FormData()
  body.append('file', file)
  return client.put<PersonRead>(`/v1/players/${String(personId)}/photo`, body)
}

// A player who never had one answers 404 `player.photo_missing`.
export function deletePlayerPhoto(client: ApiClient, personId: number): Promise<void> {
  return client.delete<void>(`/v1/players/${String(personId)}/photo`)
}

// A club's tesserati, per season. The registration is the pair, so both dates live on it.

export type ClubPlayerRead = components['schemas']['ClubPlayerRead']
/** A row of the list: the tesserato, the signed URL of the photo, and the leva of the season. */
export type ClubPlayerListItem = components['schemas']['ClubPlayerListItem']
export type ClubPlayerPage = components['schemas']['ClubPlayerPage']
export type ClubPlayerCreate = components['schemas']['ClubPlayerCreate']
export type ClubPlayerCreated = components['schemas']['ClubPlayerCreated']
export type RegistrationUpdate = components['schemas']['RegistrationUpdate']

type ListClubPlayersQuery = NonNullable<
  operations['list_club_players_v1_clubs__club_id__players_get']['parameters']['query']
>

export type ClubPlayerSort = NonNullable<ListClubPlayersQuery['sort']>
/** Released registrations are out by default: `all` is how both are asked for. */
export type RegistrationStatus = NonNullable<ListClubPlayersQuery['status']>

export type ClubPlayerFilters = {
  readonly limit?: number
  readonly offset?: number
  readonly q?: string | null
  /** The year the season opens in: 2026 is 2026/27. Omitted means the one containing today. */
  readonly season_year?: number | null
  readonly status?: RegistrationStatus | null
  readonly sort?: ClubPlayerSort
}

/** One page of a club's tesserati. The response repeats which season it answered for. */
export function listClubPlayers(
  client: ApiClient,
  clubId: number,
  filters: ClubPlayerFilters = {},
): Promise<ClubPlayerPage> {
  return client.get<ClubPlayerPage>(`/v1/clubs/${String(clubId)}/players`, { query: filters })
}

// Staff only. Person, player and registration in one call, the server reusing the person
// by their natural key. Already tesserato that season answers 409, naming no other club.
export function createClubPlayer(
  client: ApiClient,
  clubId: number,
  body: ClubPlayerCreate,
): Promise<ClubPlayerCreated> {
  return client.post<ClubPlayerCreated>(`/v1/clubs/${String(clubId)}/players`, body)
}

// The import of the tabulato: the files go both times, as the server keeps nothing. The preview
// writes nothing; the confirmation registers row by row and says what really went in.

export type TabulatoPreview = components['schemas']['TabulatoPreview']
export type TabulatoOutcome = components['schemas']['TabulatoOutcome']
export type TabulatoRow = components['schemas']['TabulatoRow']
export type TabulatoGone = components['schemas']['TabulatoGone']
export type TabulatoGonePlayer = components['schemas']['TabulatoGonePlayer']
export type TabulatoCorrection = components['schemas']['TabulatoCorrection']
export type TabulatoWarning = components['schemas']['TabulatoWarning']
export type TabulatoFileRead = components['schemas']['TabulatoFileRead']
/** A squad of the club with the annate it takes: what a file covers, or does not. */
export type TabulatoTeam = components['schemas']['TabulatoTeam']
export type TabulatoTeams = components['schemas']['TabulatoTeams']
export type TabulatoKind = NonNullable<TabulatoRow['kind']>
export type TabulatoRowWarning = TabulatoRow['warnings'][number]

/** The files in the order the reader put them in: a row is named by its file's place and its line. */
export type TabulatoFiles = readonly File[]

function tabulatoForm(files: TabulatoFiles, corrections: readonly TabulatoCorrection[]): FormData {
  const body = new FormData()
  for (const file of files) body.append('files', file)
  if (corrections.length > 0) body.append('corrections', JSON.stringify(corrections))
  return body
}

/** The club's squads with the annate each takes this season, before any file is read. */
export function getTabulatoTeams(client: ApiClient, clubId: number): Promise<TabulatoTeams> {
  return client.get<TabulatoTeams>(`/v1/clubs/${String(clubId)}/tabulato/teams`)
}

/** What the files would do. A file that is no tabulato answers 422 with its place in `params.file`. */
export function previewTabulato(
  client: ApiClient,
  clubId: number,
  files: TabulatoFiles,
  corrections: readonly TabulatoCorrection[],
): Promise<TabulatoPreview> {
  const body = tabulatoForm(files, corrections)
  return client.post<TabulatoPreview>(`/v1/clubs/${String(clubId)}/tabulato/preview`, body)
}

/** Registers the rows. Files changed since the preview's `fingerprint` answer 409
 *  `tabulato.changed`. */
export function confirmTabulato(
  client: ApiClient,
  clubId: number,
  files: TabulatoFiles,
  fingerprint: string,
  corrections: readonly TabulatoCorrection[],
): Promise<TabulatoOutcome> {
  const body = tabulatoForm(files, corrections)
  body.append('fingerprint', fingerprint)
  return client.post<TabulatoOutcome>(`/v1/clubs/${String(clubId)}/tabulato/confirm`, body)
}

/** Svincolo, and its undo: `released_on: null` gives back an active tesseramento.
 *  Idempotent, so a double click means what one click means. Never a DELETE. */
export function updateRegistration(
  client: ApiClient,
  registrationId: number,
  body: RegistrationUpdate,
): Promise<PlayerRegistrationRead> {
  return client.patch<PlayerRegistrationRead>(`/v1/registrations/${String(registrationId)}`, body)
}

// A club's organigramma. The listing carries two contacts per row and is read by staff and whoever
// holds a seat at the club; the org chart carries none.

export type AppointmentRead = components['schemas']['AppointmentRead']
export type AppointmentCreate = components['schemas']['AppointmentCreate']
export type AppointmentUpdate = components['schemas']['AppointmentUpdate']
export type AppointmentList = components['schemas']['AppointmentList']
/** What «Togli» did: the incarico on the squad it closed, and the one without a squad it opened. */
export type AppointmentUnassigned = components['schemas']['AppointmentUnassigned']

type ListAppointmentsQuery = NonNullable<
  operations['list_club_appointments_v1_clubs__club_id__appointments_get']['parameters']['query']
>

/** Derived from the response, as `AgeGroup` is: Pydantic inlines the `Literal`. */
export type AppointmentRole = AppointmentRead['role']
/** Ended incarichi are out by default: `all` is how both are asked for. */
export type AppointmentStatus = NonNullable<ListAppointmentsQuery['status']>

export type AppointmentFilters = {
  /** The year the season opens in: 2026 is 2026/27. Omitted means the one containing today. */
  readonly season_year?: number | null
  readonly status?: AppointmentStatus | null
  /** Only one squad's incarichi. Club-wide rows drop out when it is set. */
  readonly team_id?: number | null
}

export type RegisterPage = components['schemas']['RegisterPage']
/** One person and the incarichi that matched. The register lists people, not rows: a
 *  segretaria of two società is one row with two incarichi. */
export type RegisterPerson = components['schemas']['RegisterPerson']
export type AppointmentBrief = components['schemas']['AppointmentBrief']

type ListRegisterQuery = NonNullable<
  operations['list_register_v1_appointments_get']['parameters']['query']
>

export type RegisterSort = NonNullable<ListRegisterQuery['sort']>
/** One more than the organigramma has: `former` is ended here AND open nowhere. */
export type RegisterStatus = NonNullable<ListRegisterQuery['status']>

export type RegisterFilters = {
  readonly limit?: number
  readonly offset?: number
  readonly q?: string | null
  readonly club_id?: number | null
  readonly role?: AppointmentRole | null
  readonly season_year?: number | null
  readonly status?: RegisterStatus | null
  readonly sort?: RegisterSort
}

/** Who holds an incarico, across every club. The organigramma reads one club; «who has ended
 *  and is nowhere any more» is not a question a single club can answer. */
export function listAppointments(
  client: ApiClient,
  filters: RegisterFilters = {},
): Promise<RegisterPage> {
  return client.get<RegisterPage>('/v1/appointments', { query: filters })
}

/** Every incarico of the club for one season, club-wide roles first. Unpaged: an
 *  organigramma is tens of rows, not thousands. */
export function listClubAppointments(
  client: ApiClient,
  clubId: number,
  filters: AppointmentFilters = {},
): Promise<AppointmentList> {
  return client.get<AppointmentList>(`/v1/clubs/${String(clubId)}/appointments`, { query: filters })
}

export type OrgChart = components['schemas']['OrgChart']
/** An incarico as the organigramma shows it: names, role and squad, and no contact field —
 *  the type is the guarantee, so a page cannot show one by mistake. */
export type OrgChartEntry = components['schemas']['OrgChartEntry']

/** The incarichi in charge in the season containing today, one entry per incarico. */
export function getClubOrgChart(client: ApiClient, clubId: number): Promise<OrgChart> {
  return client.get<OrgChart>(`/v1/clubs/${String(clubId)}/org-chart`)
}

/** Staff for any club, a dirigente for their own. Person and appointment in one call, the
 *  server reusing the person by their natural key. Registering only: inviting is
 *  `createClubInvitation`, and `has_account` says whether the holder can sign in. */
export function createClubAppointment(
  client: ApiClient,
  clubId: number,
  body: AppointmentCreate,
): Promise<AppointmentRead> {
  return client.post<AppointmentRead>(`/v1/clubs/${String(clubId)}/appointments`, body)
}

/** Correct a role or a squad, end an incarico, or reopen it: `ended_on: null` is the undo.
 *  Never a DELETE — the row is history. */
export function updateAppointment(
  client: ApiClient,
  appointmentId: number,
  body: AppointmentUpdate,
): Promise<AppointmentRead> {
  return client.patch<AppointmentRead>(`/v1/appointments/${String(appointmentId)}`, body)
}

/** «Togli»: the person leaves the squad and its seat in one act, and the incarico on it ends rather than
 *  losing its squad, so the history keeps the period. With nothing else left open in the società they stay
 *  as staff non assegnato (`opened`). Staff, or the club's dirigente di società. */
export function unassignAppointment(
  client: ApiClient,
  appointmentId: number,
): Promise<AppointmentUnassigned> {
  return client.post<AppointmentUnassigned>(`/v1/appointments/${String(appointmentId)}/unassign`)
}

// An offer goes to an address, not to a person: the incarico is written when it is accepted.

export type InvitationCreate = components['schemas']['InvitationCreate']
export type InvitationOffer = components['schemas']['InvitationOffer']
export type InvitationOfferList = components['schemas']['InvitationOfferList']
/** An offer as just sent: `email_sent` is the mail channel's answer, not a delivery receipt. */
export type InvitationIssuedRead = components['schemas']['InvitationIssuedRead']
export type OfferState = InvitationOffer['state']

export function createClubInvitation(
  client: ApiClient,
  clubId: number,
  body: InvitationCreate,
): Promise<InvitationIssuedRead> {
  return client.post<InvitationIssuedRead>(`/v1/clubs/${String(clubId)}/invitations`, body)
}

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type InvitationFilters = { readonly season_year?: number | null }

/** Pending or expired, never accepted: an accepted offer is an incarico in the organigramma.
 *  Omit `season_year` and the server resolves today's. */
export function listClubInvitations(
  client: ApiClient,
  clubId: number,
  filters: InvitationFilters = {},
): Promise<InvitationOfferList> {
  return client.get<InvitationOfferList>(`/v1/clubs/${String(clubId)}/invitations`, {
    query: filters,
  })
}

/** A fresh link for the same offer: the previous one stops working. */
export function resendInvitation(
  client: ApiClient,
  invitationId: number,
): Promise<InvitationIssuedRead> {
  return client.post<InvitationIssuedRead>(`/v1/invitations/${String(invitationId)}/resend`, {})
}

/** A revoke and not a DELETE: the row is the record that someone was invited. 204, because
 *  `OfferState` has no word for revoked. */
export function revokeInvitation(client: ApiClient, invitationId: number): Promise<void> {
  return client.post<void>(`/v1/invitations/${String(invitationId)}/revoke`)
}

export type ActivationRead = components['schemas']['ActivationRead']
/** Which of the three ways the link takes: sign in, a known person, or a new one. */
export type ActivationPath = ActivationRead['path']
export type ActivationCreate = components['schemas']['ActivationCreate']
export type ActivationAccept = components['schemas']['ActivationAccept']
export type ActivationDone = components['schemas']['ActivationDone']

/** Authenticated, unlike the rest of `/v1/activation`: the token proves the address, only
 *  the session proves the account. */
export function acceptActivation(
  client: ApiClient,
  body: ActivationAccept,
): Promise<ActivationDone> {
  return client.post<ActivationDone>('/v1/activation/accept', body)
}

// The field installation: the box in the cabinet, and the fleet it belongs to. Staff
// only, both ways — these rows carry the SIM identifier of every installation.

export type GatewayRead = components['schemas']['GatewayRead']
export type GatewayUpdate = components['schemas']['GatewayUpdate']
export type GatewayHealthRead = components['schemas']['GatewayHealthRead']
export type GatewayList = components['schemas']['GatewayList']
export type GatewayPage = components['schemas']['GatewayPage']
export type GatewayFleetRead = components['schemas']['GatewayFleetRead']
/** A kit's condition, decided by the server on its own clock; null unless installed. */
export type KitCondition = NonNullable<GatewayRead['attention']>
/** Why a speaking kit wants a check, in display order. */
export type KitReason = GatewayRead['reasons'][number]
export type CameraRead = components['schemas']['CameraRead']
export type CameraList = components['schemas']['CameraList']
export type CameraCreate = components['schemas']['CameraCreate']
export type CameraUpdate = components['schemas']['CameraUpdate']
export type CameraStreamHealth = components['schemas']['CameraStreamHealth']

type ListGatewaysQuery = NonNullable<
  operations['list_gateways_v1_gateways_get']['parameters']['query']
>

/** The orderings the fleet accepts. A leading minus is descending. */
export type GatewaySort = NonNullable<ListGatewaysQuery['sort']>

/** Where a kit stands: in stock, installed at a pitch, or retired. */
export type KitPlacement = NonNullable<ListGatewaysQuery['placement']>

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type GatewayFilters = {
  readonly limit?: number
  readonly offset?: number
  /** Matches the CPU serial, the SIM ICCID or the hostname. */
  readonly q?: string | null
  readonly sort?: GatewaySort
  readonly placement?: KitPlacement
  /** Only the boxes that want a look; `total` counts them before paging. */
  readonly attention?: boolean
  /** The newest published version: with it, a box running another one is `outdated`. */
  readonly release?: string | null
  /** Only the boxes behind `release`; none without one. */
  readonly outdated?: boolean
}

/** One page of the whole fleet, each box with the pitch it sits at. */
export function listGateways(
  client: ApiClient,
  filters: GatewayFilters = {},
): Promise<GatewayPage> {
  return client.get<GatewayPage>('/v1/gateways', { query: filters })
}

/** Every gateway ever installed at one pitch, the removed ones included: removed is a
 * date, so the box that filmed last season stays in the list. Unpaged — a campo has one. */
export function listPitchGateways(client: ApiClient, pitchId: number): Promise<GatewayList> {
  return client.get<GatewayList>(`/v1/pitches/${String(pitchId)}/gateways`)
}

/** One box. A 404 carries `gateway.not_found` with the id in `params`. */
export function getGateway(client: ApiClient, gatewayId: number): Promise<GatewayRead> {
  return client.get<GatewayRead>(`/v1/gateways/${String(gatewayId)}`)
}

/** The cameras hanging off one box. Unpaged: a Duo 3 is one camera with two streams. */
export function listGatewayCameras(client: ApiClient, gatewayId: number): Promise<CameraList> {
  return client.get<CameraList>(`/v1/gateways/${String(gatewayId)}/cameras`)
}

/** Staff only. A camera on a kit, usually at the bench. A MAC, serial or UID another camera
 * already has answers 409 `camera.mac_address_taken` / `camera.serial_number_taken` /
 * `camera.uid_taken`. */
export function createCamera(
  client: ApiClient,
  gatewayId: number,
  body: CameraCreate,
): Promise<CameraRead> {
  return client.post<CameraRead>(`/v1/gateways/${String(gatewayId)}/cameras`, body)
}

/** Staff only. What the label says: UID, serial, model. A UID or serial another camera
 * already has answers 409 `camera.uid_taken` / `camera.serial_number_taken`. */
export function updateCamera(
  client: ApiClient,
  cameraId: number,
  body: CameraUpdate,
): Promise<CameraRead> {
  return client.patch<CameraRead>(`/v1/cameras/${String(cameraId)}`, body)
}

/** Staff only. The three things that happen to an installed box: it gets a SIM, it moves
 * to another campo, it is retired. The hostname follows the move by itself. `hardware_id`
 * is not offered: swapping a kit's board is a gesture still to build. */
export function updateGateway(
  client: ApiClient,
  gatewayId: number,
  body: GatewayUpdate,
): Promise<GatewayRead> {
  return client.patch<GatewayRead>(`/v1/gateways/${String(gatewayId)}`, body)
}

// What a box runs (`health.app_version`) and what a person asked it to run
// (`target_version`). The box converges within a quarter of an hour, unless it is busy.

export type ReleaseRead = components['schemas']['ReleaseRead']

/** The newest gateway release published to *this* environment. A 404 carries
 * `gateway.release_not_found` and means nothing has been published yet, which on prod also
 * covers «built on staging and never promoted». */
export function getLatestGatewayRelease(client: ApiClient): Promise<ReleaseRead> {
  return client.get<ReleaseRead>('/v1/gateway-releases/latest')
}

/** Staff only. Records the request and returns at once: nothing has happened on the box.
 * A version this environment never published is refused here rather than hours later in
 * the box's journal — `gateway.release_not_found`, with the version in `params`. */
export function upgradeGateway(
  client: ApiClient,
  gatewayId: number,
  version: string,
): Promise<GatewayRead> {
  return client.post<GatewayRead>(`/v1/gateways/${String(gatewayId)}/upgrade`, { version })
}

export type FleetUpgradeRead = components['schemas']['FleetUpgradeRead']

/** Staff only. The same request to every kit that spoke and runs another version, skipping
 * the ones already asked for it: `asked` counts the kits reached. An unpublished version is
 * `gateway.release_not_found`, as for one box. */
export function upgradeOutdatedGateways(
  client: ApiClient,
  version: string,
): Promise<FleetUpgradeRead> {
  return client.post<FleetUpgradeRead>('/v1/gateways/upgrade', { version })
}

/** Withdraws a request the box has not acted on yet. A box already upgraded stays where it
 * is: clearing the column sends nothing backwards. */
export function cancelGatewayUpgrade(client: ApiClient, gatewayId: number): Promise<GatewayRead> {
  return client.post<GatewayRead>(`/v1/gateways/${String(gatewayId)}/upgrade/cancel`, {})
}

// Maintenance (spec 2026-10-21 § 7). Types read off the paths, as the bench's.

type MaintenanceGet = paths['/v1/gateways/{gateway_id}/maintenance']['get']
type MaintenancePost = paths['/v1/gateways/{gateway_id}/maintenance']['post']
type MaintenanceEnvelope = MaintenancePost['responses'][201]['content']['application/json']

export type MaintenanceList = MaintenanceGet['responses'][200]['content']['application/json']
export type MaintenanceRequestRead = MaintenanceList['items'][number]
export type MaintenanceAction = MaintenanceRequestRead['action']
/** What staff may ask for: every action but the two adopts, which only an intervention opens. */
export type AskedMaintenanceAction =
  MaintenancePost['requestBody']['content']['application/json']['action']
export type MaintenanceStatus = MaintenanceRequestRead['status']

/** Staff only. The latest twenty requests of one kit, newest first. */
export function listMaintenance(client: ApiClient, gatewayId: number): Promise<MaintenanceList> {
  return client.get<MaintenanceList>(`/v1/gateways/${String(gatewayId)}/maintenance`)
}

/** Staff only. Nothing happens now: the box picks it up on its next heartbeat. The same action
 * still open answers 409 `gateway.maintenance_pending`. */
export async function requestMaintenance(
  client: ApiClient,
  gatewayId: number,
  action: AskedMaintenanceAction,
): Promise<MaintenanceRequestRead> {
  const answer = await client.post<MaintenanceEnvelope>(
    `/v1/gateways/${String(gatewayId)}/maintenance`,
    { action },
  )
  return answer.request
}

// A kit's interventions (spec 2026-10-28): a part swapped, or the kit retired.

type InterventionsGet = paths['/v1/gateways/{gateway_id}/interventions']['get']
type InterventionsPost = paths['/v1/gateways/{gateway_id}/interventions']['post']
type RetirePost = paths['/v1/gateways/{gateway_id}/retire']['post']
type InterventionEnvelope = InterventionsPost['responses'][201]['content']['application/json']

export type InterventionList = InterventionsGet['responses'][200]['content']['application/json']
export type InterventionRead = InterventionList['items'][number]
export type InterventionItemRead = InterventionRead['items'][number]
export type InterventionState = InterventionRead['state']
export type PartOutcome = InterventionItemRead['outcome']
export type ReplacementCreate = NonNullable<
  InterventionsPost['requestBody']
>['content']['application/json']
export type RetirementCreate = NonNullable<RetirePost['requestBody']>['content']['application/json']

/** Staff only. The latest twenty of one kit, newest first. */
export function listInterventions(client: ApiClient, gatewayId: number): Promise<InterventionList> {
  return client.get<InterventionList>(`/v1/gateways/${String(gatewayId)}/interventions`)
}

/** Staff only. A kit with one still open answers 409 `intervention.open`. */
export async function openReplacement(
  client: ApiClient,
  gatewayId: number,
  body: ReplacementCreate,
): Promise<InterventionRead> {
  const answer = await client.post<InterventionEnvelope>(
    `/v1/gateways/${String(gatewayId)}/interventions`,
    body,
  )
  return answer.intervention
}

/** Staff only. Closed at once; every part of the kit needs one outcome
 * (422 `intervention.parts_mismatch`). */
export async function retireGateway(
  client: ApiClient,
  gatewayId: number,
  body: RetirementCreate,
): Promise<InterventionRead> {
  const answer = await client.post<InterventionEnvelope>(
    `/v1/gateways/${String(gatewayId)}/retire`,
    body,
  )
  return answer.intervention
}

/** Staff only. Only one nothing has started on (409 `intervention.not_cancellable`). */
export async function cancelIntervention(
  client: ApiClient,
  interventionId: number,
): Promise<InterventionRead> {
  const answer = await client.post<InterventionEnvelope>(
    `/v1/interventions/${String(interventionId)}/cancel`,
  )
  return answer.intervention
}

// The bench (spec 2026-10-12 § 13). Types read off the paths: the schema names are the API's
// to choose, the paths are the contract.

type BenchBoxesGet = paths['/v1/bench/boxes']['get']
type BenchPreparePost = paths['/v1/bench/boxes/{hardware_id}/prepare']['post']
type BenchRetryPost = paths['/v1/bench/jobs/{job_id}/retry']['post']
type GatewaySecretsGet = paths['/v1/gateways/{gateway_id}/secrets']['get']

export type BenchBoxList = BenchBoxesGet['responses'][200]['content']['application/json']
export type BenchBoxRead = BenchBoxList['items'][number]
export type BenchJobRead = NonNullable<BenchBoxRead['job']>
export type BenchStepRead = BenchJobRead['steps'][number]
export type BenchJobState = BenchJobRead['state']
export type BenchStepName = BenchStepRead['step']
export type BenchStepStatus = BenchStepRead['status']
export type BenchPrepare = BenchPreparePost['requestBody']['content']['application/json']
export type BenchPrepared = BenchPreparePost['responses'][201]['content']['application/json']
export type BenchRetried = BenchRetryPost['responses'][200]['content']['application/json']
export type GatewaySecretsRead = GatewaySecretsGet['responses'][200]['content']['application/json']

/** Staff only. The boxes heard in the last hour, each with its current job or none. */
export function listBenchBoxes(client: ApiClient): Promise<BenchBoxList> {
  return client.get<BenchBoxList>('/v1/bench/boxes')
}

/** Staff only. A code that is not the box's router answers 409 `bench.router_code_mismatch`,
 * a box already working 409 `bench.box_busy`, a box no longer heard 404 `bench.box_not_found`. */
type BenchRepairsGet = paths['/v1/bench/repairs']['get']
type BenchRepairPost = paths['/v1/bench/boxes/{hardware_id}/repair']['post']

export type BenchRepairList = BenchRepairsGet['responses'][200]['content']['application/json']
export type BenchRepairRead = BenchRepairList['items'][number]
export type BenchRepair = BenchRepairPost['requestBody']['content']['application/json']

/** Staff only. The kits waiting at the Banco for a new board, or for their own with a new SD. */
export function listBenchRepairs(client: ApiClient): Promise<BenchRepairList> {
  return client.get<BenchRepairList>('/v1/bench/repairs')
}

/** Staff only. Offers the box the job of a kit's new board; 409 `bench.no_repair_pending` when
 * the kit is not waiting for one. */
export function repairBenchBox(
  client: ApiClient,
  hardwareId: string,
  body: BenchRepair,
): Promise<BenchPrepared> {
  return client.post<BenchPrepared>(
    `/v1/bench/boxes/${encodeURIComponent(hardwareId)}/repair`,
    body,
  )
}

export function prepareBenchBox(
  client: ApiClient,
  hardwareId: string,
  body: BenchPrepare,
): Promise<BenchPrepared> {
  return client.post<BenchPrepared>(
    `/v1/bench/boxes/${encodeURIComponent(hardwareId)}/prepare`,
    body,
  )
}

/** Staff only. The failed steps go back to «to do»; a failed «pi» also gets a new Tailscale key. */
export function retryBenchJob(client: ApiClient, jobId: number): Promise<BenchRetried> {
  return client.post<BenchRetried>(`/v1/bench/jobs/${String(jobId)}/retry`, {})
}

/** Staff only. The passwords the bench generated for the kit: console, router and camera. */
export function getGatewaySecrets(
  client: ApiClient,
  gatewayId: number,
): Promise<GatewaySecretsRead> {
  return client.get<GatewaySecretsRead>(`/v1/gateways/${String(gatewayId)}/secrets`)
}

// The sign-up queue: a club's president asks to enter, and a Volée operator decides. Staff
// only, both ways — the applicant reads their own request through `/v1/registration-requests/me`.

export type RegistrationRequestRead = components['schemas']['RegistrationRead']
export type RegistrationRequestPage = components['schemas']['RegistrationPage']
export type RegistrationRequestReview = components['schemas']['RegistrationReview']
export type RegistrationDecision = RegistrationRequestReview['decision']

/** Derived from the contract, which types the filter as a union since the backend does:
 * a hand-written copy of these four values would be free to drift from it. */
export type RegistrationRequestStatus = NonNullable<
  NonNullable<
    operations['list_requests_v1_registration_requests_get']['parameters']['query']
  >['status']
>

/** The four, in the order an operator works them. The type comes from the contract above,
 * so a value renamed there stops compiling here. */
export const REGISTRATION_REQUEST_STATUSES: readonly RegistrationRequestStatus[] = [
  'submitted',
  'needs_integration',
  'approved',
  'rejected',
]

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type RegistrationRequestFilters = {
  readonly limit?: number
  readonly offset?: number
  readonly status?: RegistrationRequestStatus | null
}

/** One page of requests, plus the total of everything the filter matches. */
export function listRegistrationRequests(
  client: ApiClient,
  filters: RegistrationRequestFilters = {},
): Promise<RegistrationRequestPage> {
  return client.get<RegistrationRequestPage>('/v1/registration-requests', { query: filters })
}

/** One request by its id, for the detail screen. */
export function getRegistrationRequest(
  client: ApiClient,
  requestId: number,
): Promise<RegistrationRequestRead> {
  return client.get<RegistrationRequestRead>(`/v1/registration-requests/${String(requestId)}`)
}

/** The three decisions on one endpoint. `approved` and `rejected` are terminal: a second
 * review answers 409 `registration.already_decided`, and refusing without a reason is a 422. */
export function reviewRegistrationRequest(
  client: ApiClient,
  requestId: number,
  body: RegistrationRequestReview,
): Promise<RegistrationRequestRead> {
  return client.post<RegistrationRequestRead>(
    `/v1/registration-requests/${String(requestId)}/review`,
    body,
  )
}

// Leagues — the categories, the gironi that carve a season up, and the entries binding a
// team to one. Writing is staff; the four listings read to anyone authenticated, candidates apart.

export type LeagueCategoryRead = components['schemas']['LeagueCategoryRead']
export type LeagueCategoryRow = components['schemas']['LeagueCategoryRow']
export type LeagueCategoryCreate = components['schemas']['LeagueCategoryCreate']
export type LeagueCategoryUpdate = components['schemas']['LeagueCategoryUpdate']
export type LeagueCategoryList = components['schemas']['LeagueCategoryList']
export type LeagueGroupRead = components['schemas']['LeagueGroupRead']
export type LeagueGroupCreate = components['schemas']['LeagueGroupCreate']
export type LeagueGroupUpdate = components['schemas']['LeagueGroupUpdate']
export type LeagueGroupList = components['schemas']['LeagueGroupList']
export type EnrollmentRead = components['schemas']['EnrollmentRead']
export type EnrollmentCreate = components['schemas']['EnrollmentCreate']
export type EnrollmentUpdate = components['schemas']['EnrollmentUpdate']
export type EnrollmentList = components['schemas']['EnrollmentList']
export type EnrollmentCandidate = components['schemas']['EnrollmentCandidate']
export type EnrollmentCandidateList = components['schemas']['EnrollmentCandidateList']

/** Derived from the response, as `ClubStatus` is: Pydantic inlines the `Literal`. */
export type LeagueTier = LeagueCategoryRead['tier']

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type LeagueGroupFilters = {
  /** The year the season opens in: 2026 is 2026/27. Omitted means the one containing today. */
  readonly season_year?: number | null
  readonly league_category_id?: number | null
}

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type CandidateFilters = {
  /** Matches the club's name. */
  readonly q?: string | null
  readonly limit?: number
}

/** Every category, unpaged: the picker wants them all. */
export function listLeagueCategories(client: ApiClient): Promise<LeagueCategoryList> {
  return client.get<LeagueCategoryList>('/v1/league-categories')
}

/** Staff only. The name is free text; the duplicate the database refuses is the quadruple
 * of `age_group`/`under_age`/`tier`/`gender`, not the name. */
export function createLeagueCategory(
  client: ApiClient,
  body: LeagueCategoryCreate,
): Promise<LeagueCategoryRow> {
  return client.post<LeagueCategoryRow>('/v1/league-categories', body)
}

/** Staff only. Renaming stays free; correcting one of the four key columns answers 422
 * `league_category.fields_locked_by_groups` once a girone has been built on it. */
export function updateLeagueCategory(
  client: ApiClient,
  categoryId: number,
  body: LeagueCategoryUpdate,
): Promise<LeagueCategoryRow> {
  return client.patch<LeagueCategoryRow>(`/v1/league-categories/${String(categoryId)}`, body)
}

/** Staff only. No pre-check: a category with a girone is refused by the FK, as a 409. */
export function deleteLeagueCategory(client: ApiClient, categoryId: number): Promise<void> {
  return client.delete<void>(`/v1/league-categories/${String(categoryId)}`)
}

/** The gironi of one season. A year nobody has written into has no gironi, never a 404. */
export function listLeagueGroups(
  client: ApiClient,
  filters: LeagueGroupFilters = {},
): Promise<LeagueGroupList> {
  return client.get<LeagueGroupList>('/v1/league-groups', { query: filters })
}

/** One girone. */
export function getLeagueGroup(client: ApiClient, groupId: number): Promise<LeagueGroupRead> {
  return client.get<LeagueGroupRead>(`/v1/league-groups/${String(groupId)}`)
}

/** Staff only. The first girone of an annata is the first write into it, so the season is
 * created here. */
export function createLeagueGroup(
  client: ApiClient,
  body: LeagueGroupCreate,
): Promise<LeagueGroupRead> {
  return client.post<LeagueGroupRead>('/v1/league-groups', body)
}

/** Staff only. The code alone — the category and the season are the girone's identity. */
export function updateLeagueGroup(
  client: ApiClient,
  groupId: number,
  body: LeagueGroupUpdate,
): Promise<LeagueGroupRead> {
  return client.patch<LeagueGroupRead>(`/v1/league-groups/${String(groupId)}`, body)
}

/** Staff only. No pre-check: a girone with entries is refused by the FK, as a 409. */
export function deleteLeagueGroup(client: ApiClient, groupId: number): Promise<void> {
  return client.delete<void>(`/v1/league-groups/${String(groupId)}`)
}

/** The teams entered in a girone. Unpaged: twelve to sixteen sides, not a page of them. */
export function listGroupEnrollments(client: ApiClient, groupId: number): Promise<EnrollmentList> {
  return client.get<EnrollmentList>(`/v1/league-groups/${String(groupId)}/enrollments`)
}

export type TeamEnrollmentRead = components['schemas']['TeamEnrollmentRead']

export type ClubEnrollmentList = components['schemas']['ClubEnrollmentList']

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type EnrollmentFilters = { readonly season_year?: number | null }

/** Which gironi a società's squads are entered in. Any authenticated caller; a year nobody
 *  has written into is an empty list and never a 404, and withdrawn entries come back
 *  flagged rather than filtered. Omit `season_year` and the server resolves today's. */
export function listClubEnrollments(
  client: ApiClient,
  clubId: number,
  filters: EnrollmentFilters = {},
): Promise<ClubEnrollmentList> {
  return client.get<ClubEnrollmentList>(`/v1/clubs/${String(clubId)}/enrollments`, {
    query: filters,
  })
}

/** The club's teams this girone could still take. */
export function listGroupCandidates(
  client: ApiClient,
  groupId: number,
  filters: CandidateFilters = {},
): Promise<EnrollmentCandidateList> {
  return client.get<EnrollmentCandidateList>(`/v1/league-groups/${String(groupId)}/candidates`, {
    query: filters,
  })
}

/** Staff only. A duplicate becomes a 409 `enrollment.already_exists` via the unique
 * constraint on the girone/team pair. */
export function enrollTeam(
  client: ApiClient,
  groupId: number,
  body: EnrollmentCreate,
): Promise<EnrollmentRead> {
  return client.post<EnrollmentRead>(`/v1/league-groups/${String(groupId)}/enrollments`, body)
}

/** Withdraw a team, or undo a mistaken withdrawal: `has_withdrawn: false` is the same
 * endpoint as the undo, not a separate call. Staff only. */
export function updateEnrollment(
  client: ApiClient,
  entryId: number,
  body: EnrollmentUpdate,
): Promise<EnrollmentRead> {
  return client.patch<EnrollmentRead>(`/v1/enrollments/${String(entryId)}`, body)
}

/** Staff only. No pre-check: `match`, `call_up` and `team_metric` all point at an entry
 * with RESTRICT, so a girone that has started refuses on its own as a 409. */
export function removeEnrollment(client: ApiClient, entryId: number): Promise<void> {
  return client.delete<void>(`/v1/enrollments/${String(entryId)}`)
}

export type MatchRead = components['schemas']['MatchRead']
export type MatchSide = components['schemas']['MatchSide']
export type MatchPitch = components['schemas']['MatchPitch']
export type MatchCreate = components['schemas']['MatchCreate']
export type MatchUpdate = components['schemas']['MatchUpdate']
export type MatchList = components['schemas']['MatchList']
export type ClubMatchList = components['schemas']['ClubMatchList']
export type TeamMatchList = components['schemas']['TeamMatchList']
export type MatchOverviewRead = components['schemas']['MatchOverviewRead']
export type MatchOverviewPage = components['schemas']['MatchOverviewPage']
export type MatchRecordingSummary = components['schemas']['MatchRecordingSummary']
export type StandingsRow = components['schemas']['StandingsRow']
export type StandingsList = components['schemas']['StandingsList']
export type RecordingRead = components['schemas']['RecordingRead']
export type RecordingList = components['schemas']['RecordingList']
export type RecordingStreamRead = components['schemas']['RecordingStreamRead']
export type PlaybackRead = components['schemas']['PlaybackRead']
export type PlaybackSegment = components['schemas']['PlaybackSegment']
export type PlaybackTokenRead = components['schemas']['PlaybackTokenRead']

/** Derived from the response, as `MatchStatus` is: Pydantic inlines the `Literal`. */
export type RecordingStatus = RecordingRead['status']
export type MatchAnalysisRead = components['schemas']['MatchAnalysisRead']
export type MatchAnalysisState = MatchAnalysisRead['status']
export type MatchAnalysisSendRead = components['schemas']['MatchAnalysisSendRead']
export type AnalysisSwitchRead = components['schemas']['AnalysisSwitchRead']
export type MatchStatsRead = components['schemas']['MatchStatsRead']
export type TeamStatsRead = components['schemas']['TeamStatsRead']
export type PlayerStatsRead = components['schemas']['PlayerStatsRead']
export type PlayerMatchStatsList = components['schemas']['PlayerMatchStatsList']
export type PlayerMatchStatsRead = components['schemas']['PlayerMatchStatsRead']
/** A provider's numbers under its own metric names: which of them a page shows is the app's catalogue. */
export type ProviderStats = TeamStatsRead['stats']
export type SegmentQuality = RecordingStreamRead['quality']

/** Derived from the response, as `LeagueTier` is: Pydantic inlines the `Literal`. */
export type MatchStatus = MatchRead['status']
export type MatchLeg = MatchRead['leg']

/** The subset a PATCH accepts, and the omission is the contract: the back-office
 * schedules and postpones, never scores. Derived, so a third writable state reaches the
 * select instead of being dropped. */
export type WritableMatchStatus = NonNullable<MatchUpdate['status']>

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type MatchFilters = {
  readonly matchday?: number | null
  readonly leg?: MatchLeg | null
  readonly status?: MatchStatus | null
}

/** The calendar of a girone. Unpaged: it is one document, not a page of one. */
export function listGroupMatches(
  client: ApiClient,
  groupId: number,
  filters: MatchFilters = {},
): Promise<MatchList> {
  return client.get<MatchList>(`/v1/league-groups/${String(groupId)}/matches`, { query: filters })
}

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type MatchOverviewFilters = {
  readonly limit?: number
  readonly offset?: number
  readonly pitch_id?: number | null
  /** Either side. */
  readonly club_id?: number | null
  readonly status?: MatchStatus | null
  /** `true` keeps the fixtures with at least one recording session, `false` those with none. */
  readonly recorded?: boolean | null
  /** ISO date-time bounds on the kick-off, both with an offset; `to` is exclusive. */
  readonly from?: string | null
  readonly to?: string | null
}

/** Staff only. Every fixture across gironi, a page at a time, each with its video sessions. */
export function listMatches(
  client: ApiClient,
  filters: MatchOverviewFilters = {},
): Promise<MatchOverviewPage> {
  return client.get<MatchOverviewPage>('/v1/matches', { query: filters })
}

/** Staff only. Born `scheduled`, and the score is not part of the payload. */
export function createMatch(
  client: ApiClient,
  groupId: number,
  body: MatchCreate,
): Promise<MatchRead> {
  return client.post<MatchRead>(`/v1/league-groups/${String(groupId)}/matches`, body)
}

/** One fixture. Any authenticated caller: a calendar is printed on the comunicato, and a
 * fixture carries no personal data — the team sheet, which does, is somewhere else. */
export function getMatch(client: ApiClient, matchId: number): Promise<MatchRead> {
  return client.get<MatchRead>(`/v1/matches/${String(matchId)}`)
}

/** Staff only. The two sides are absent from `MatchUpdate`: they are the match's
 * identity, and correcting the wrong pairing is a delete and a create. */
export function updateMatch(
  client: ApiClient,
  matchId: number,
  body: MatchUpdate,
): Promise<MatchRead> {
  return client.patch<MatchRead>(`/v1/matches/${String(matchId)}`, body)
}

/** Staff only. A played match answers 409 `match.already_played`, and one that was
 * filmed 409 `match.in_use`. */
export function removeMatch(client: ApiClient, matchId: number): Promise<void> {
  return client.delete<void>(`/v1/matches/${String(matchId)}`)
}

export type MatchChangeReportCreate = components['schemas']['MatchChangeReportCreate']
export type MatchChangeReportRead = components['schemas']['MatchChangeReportRead']
export type MatchChangeReportList = components['schemas']['MatchChangeReportList']
export type MatchChangeReportStatus = MatchChangeReportRead['status']

/** The changes reported on a match, newest first. An incaricato of either side, or staff: anyone else
 * gets 403 `auth.not_your_match`. */
export function listMatchChangeReports(
  client: ApiClient,
  matchId: number,
): Promise<MatchChangeReportList> {
  return client.get<MatchChangeReportList>(`/v1/matches/${String(matchId)}/change-reports`)
}

/** Tells Volée the match moves (spec #404). A played match answers 409
 * `match_change_report.match_played`. */
export function createMatchChangeReport(
  client: ApiClient,
  matchId: number,
  body: MatchChangeReportCreate,
): Promise<MatchChangeReportRead> {
  return client.post<MatchChangeReportRead>(`/v1/matches/${String(matchId)}/change-reports`, body)
}

/** Staff only. What the cameras got of a fixture: one row per session, and the stream the
 * pitch uploads. An empty list means no box was at that pitch, not an error. */
export function listMatchRecordings(client: ApiClient, matchId: number): Promise<RecordingList> {
  return client.get<RecordingList>(`/v1/matches/${String(matchId)}/recordings`)
}

/** Staff only. One stream of one session in playback order — what has arrived, never what
 * will — plus the Worker that serves it and one permission for every segment. */
export function getRecordingPlayback(
  client: ApiClient,
  recordingId: number,
  quality: SegmentQuality,
): Promise<PlaybackRead> {
  return client.get<PlaybackRead>(`/v1/recordings/${String(recordingId)}/playback`, {
    query: { quality },
  })
}

export const PLAYBACK_LIFETIMES = ['short', 'long'] as const
export type PlaybackLifetime = (typeof PLAYBACK_LIFETIMES)[number]

/** Staff only. A new permission for one session's segments, asked for before the one the
 * player holds runs out — so a long watch is never cut. */
export function getPlaybackToken(
  client: ApiClient,
  recordingId: number,
  lifetime: PlaybackLifetime,
): Promise<PlaybackTokenRead> {
  return client.get<PlaybackTokenRead>(`/v1/recordings/${String(recordingId)}/playback-token`, {
    query: { lifetime },
  })
}

/** Staff only. Takes the session, its segments and the video off the bucket, and none of
 * it comes back. The fixture is a second act: `removeMatch` stops answering 409
 * `match.in_use` once nothing films it. */
export function removeRecording(client: ApiClient, recordingId: number): Promise<void> {
  return client.delete<void>(`/v1/recordings/${String(recordingId)}`)
}

/** Staff only. Where a fixture stands at the analysis provider; `not_submitted` until it is sent. */
export function getMatchAnalysis(client: ApiClient, matchId: number): Promise<MatchAnalysisRead> {
  return client.get<MatchAnalysisRead>(`/v1/matches/${String(matchId)}/analysis`)
}

/** Any session. Empty lists until an analysis is imported; `players` holds only the sides whose
 * club the caller works at. */
export function getMatchStats(client: ApiClient, matchId: number): Promise<MatchStatsRead> {
  return client.get<MatchStatsRead>(`/v1/matches/${String(matchId)}/stats`)
}

/** Whoever may read the person's card. A reader from outside the club gets only the matches the
 * player's switches open; 403 `profile.not_public` on a closed profile. */
export function getPlayerMatchStats(
  client: ApiClient,
  personId: number,
): Promise<PlayerMatchStatsList> {
  return client.get<PlayerMatchStatsList>(`/v1/people/${String(personId)}/match-stats`)
}

export type PersonHighlightsRead = components['schemas']['PersonHighlightsRead']
export type HighlightMatchRead = components['schemas']['HighlightMatchRead']
export type HighlightClipRead = components['schemas']['HighlightClipRead']
export type HighlightMontageRead = components['schemas']['HighlightMontageRead']
/** What an analysis recognised, as `action.type` holds it: a clip's kind is one. */
export type ActionType = HighlightClipRead['type']
export type HighlightSettingsRead = components['schemas']['HighlightSettingsRead']
export type HighlightSettingsWrite = components['schemas']['HighlightSettingsWrite']

/** Who reads what, and the clips the plan locks: services/api/docs/highlights.md § «La lettura».
 * Each montage's Worker permission lapses at `token_expires_at`: read it again before then. */
export function getPersonHighlights(
  client: ApiClient,
  personId: number,
): Promise<PersonHighlightsRead> {
  return client.get<PersonHighlightsRead>(`/v1/people/${String(personId)}/highlights`)
}

export type HighlightLibraryRead = components['schemas']['HighlightLibraryRead']
export type HighlightPlaylistRead = components['schemas']['HighlightPlaylistRead']
export type PlaylistCreate = components['schemas']['PlaylistCreate']

// The library: the player and their tutori with the plan write it (403 otherwise), and every
// write answers the read, to put back in its cache key.

function libraryPath(personId: number): string {
  return `/v1/people/${String(personId)}/highlights`
}

/** The whole showcase, in its order, at most 6; empty gives it back to the latest clips. */
export function chooseShowcase(
  client: ApiClient,
  personId: number,
  actionIds: readonly number[],
): Promise<PersonHighlightsRead> {
  return client.put<PersonHighlightsRead>(`${libraryPath(personId)}/showcase`, {
    action_ids: [...actionIds],
  })
}

export function addFavourite(
  client: ApiClient,
  personId: number,
  actionId: number,
): Promise<PersonHighlightsRead> {
  return client.put<PersonHighlightsRead>(`${libraryPath(personId)}/favourites/${String(actionId)}`)
}

export function removeFavourite(
  client: ApiClient,
  personId: number,
  actionId: number,
): Promise<PersonHighlightsRead> {
  return client.delete<PersonHighlightsRead>(
    `${libraryPath(personId)}/favourites/${String(actionId)}`,
  )
}

/** «Nuova playlist», from the clip it was asked for: the new one is the last of the playlists. */
export function createPlaylist(
  client: ApiClient,
  personId: number,
  body: PlaylistCreate,
): Promise<PersonHighlightsRead> {
  return client.post<PersonHighlightsRead>(`${libraryPath(personId)}/playlists`, body)
}

export function addToPlaylist(
  client: ApiClient,
  personId: number,
  playlistId: number,
  actionId: number,
): Promise<PersonHighlightsRead> {
  return client.put<PersonHighlightsRead>(
    `${libraryPath(personId)}/playlists/${String(playlistId)}/clips/${String(actionId)}`,
  )
}

export function removeFromPlaylist(
  client: ApiClient,
  personId: number,
  playlistId: number,
  actionId: number,
): Promise<PersonHighlightsRead> {
  return client.delete<PersonHighlightsRead>(
    `${libraryPath(personId)}/playlists/${String(playlistId)}/clips/${String(actionId)}`,
  )
}

/** The same list as a reader from outside reads it: the owner's preview, under its own cache key. */
export function getPublicPlayerMatchStats(
  client: ApiClient,
  personId: number,
): Promise<PlayerMatchStatsList> {
  return client.get<PlayerMatchStatsList>(`/v1/people/${String(personId)}/match-stats`, {
    query: { as: 'public' },
  })
}

/** Staff only. `available` is the environment's switch, `on` the back office's: sending needs both. */
export function getAnalysisSwitch(client: ApiClient): Promise<AnalysisSwitchRead> {
  return client.get<AnalysisSwitchRead>('/v1/match-analysis/switch')
}

/** Staff only. Off always works; on answers 409 `match_analysis.switch_unavailable` where the
 * environment keeps the analysis off (staging and prod until UbiTrack's tokens). */
export function setAnalysisSwitch(client: ApiClient, on: boolean): Promise<AnalysisSwitchRead> {
  return client.put<AnalysisSwitchRead>('/v1/match-analysis/switch', { on })
}

/** Staff only. How many clips of a match are free, and how many days a clip lives. */
export function getHighlightSettings(client: ApiClient): Promise<HighlightSettingsRead> {
  return client.get<HighlightSettingsRead>('/v1/highlight-settings')
}

/** Staff only. Both settings together; one out of its range is a 422 on its field. */
export function setHighlightSettings(
  client: ApiClient,
  body: HighlightSettingsWrite,
): Promise<HighlightSettingsRead> {
  return client.put<HighlightSettingsRead>('/v1/highlight-settings', body)
}

/** Staff only. Sends a fixture, or again when its convocazione or video changed or it failed; else
 * `sent` is false. 409 `match_analysis.no_video`, `.no_published_team_sheet` while either is
 * missing, `.unknown_match_format` for a category with no known format. */
export function submitMatchAnalysis(
  client: ApiClient,
  matchId: number,
): Promise<MatchAnalysisSendRead> {
  return client.post<MatchAnalysisSendRead>(`/v1/matches/${String(matchId)}/analysis`)
}

/** The standings of a girone. Withdrawn sides are absent, and `position` is this
 * ordering and not the FIGC classifica avulsa. */
export function listGroupStandings(client: ApiClient, groupId: number): Promise<StandingsList> {
  return client.get<StandingsList>(`/v1/league-groups/${String(groupId)}/standings`)
}

/** An alias and not an interface, for the index signature `QueryParams` requires. */
export type CalendarFilters = {
  readonly status?: MatchStatus | null
  /** ISO date-time lower bound on the kick-off. «Upcoming» needs it as well as the status:
   *  a scheduled fixture nobody marked played is in the past. */
  readonly from?: string | null
}

/** Every fixture a club plays, on either side and across its gironi. */
export function listClubMatches(
  client: ApiClient,
  clubId: number,
  filters: CalendarFilters = {},
): Promise<ClubMatchList> {
  return client.get<ClubMatchList>(`/v1/clubs/${String(clubId)}/matches`, { query: filters })
}

export function listTeamMatches(
  client: ApiClient,
  teamId: number,
  filters: CalendarFilters = {},
): Promise<TeamMatchList> {
  return client.get<TeamMatchList>(`/v1/teams/${String(teamId)}/matches`, { query: filters })
}

// La convocazione. One per side of a match: `enrollment_id` picks which, the way `team_sheet`
// itself is keyed. A draft needs the incarico; once published the squad reads it (docs/adr/0002).

export type TeamSheetRead = components['schemas']['TeamSheetRead']
export type TeamSheetPut = components['schemas']['TeamSheetPut']
export type TeamSheetStaffRead = components['schemas']['TeamSheetStaffRead']
export type TeamSheetStaffCreate = components['schemas']['TeamSheetStaffCreate']
export type CallUpRead = components['schemas']['CallUpRead']
/** A row with its answer: only for whoever is entitled to it, so `'answer' in callUp` tells it apart. */
export type CallUpAnsweredRead = components['schemas']['CallUpAnsweredRead']
export type CallUpAnswer = components['schemas']['CallUpAnswerPut']['answer']
export type AnsweredByRead = components['schemas']['AnsweredByRead']
export type CallUpCreate = components['schemas']['CallUpCreate']
type CallUpAnswerPut = components['schemas']['CallUpAnswerPut']

/** La divisa di gara as worn: the choice and a copy of its colours, `null` while «non indicata». */
export type MatchKitRead = components['schemas']['MatchKitRead']
/** `first` or `second` by name — the server copies the società's kit — or `other` with colours. */
export type MatchKitPut = NonNullable<TeamSheetPut['match_kit']>
export type MatchKitChoice = MatchKitRead['choice']

// An alias for `PersonFilters`' reason: only an alias gets the index signature QueryParams wants.
export type TeamSheetSide = {
  readonly enrollment_id: number
}

/** A side with no convocazione yet answers an empty draft, not a 404: unwritten is still that side's. */
export function readTeamSheet(
  client: ApiClient,
  matchId: number,
  enrollmentId: number,
): Promise<TeamSheetRead> {
  const side: TeamSheetSide = { enrollment_id: enrollmentId }
  return client.get<TeamSheetRead>(`/v1/matches/${String(matchId)}/team-sheet`, { query: side })
}

/** Replaces the whole side: the call-ups and staff sent are the convocazione afterwards. */
export function putTeamSheet(
  client: ApiClient,
  matchId: number,
  enrollmentId: number,
  body: TeamSheetPut,
): Promise<TeamSheetRead> {
  const side: TeamSheetSide = { enrollment_id: enrollmentId }
  return client.put<TeamSheetRead>(`/v1/matches/${String(matchId)}/team-sheet`, body, {
    query: side,
  })
}

/** Refused with nobody called up: publishing is what tells the convocati, and one that calls nobody tells nobody. */
export function publishTeamSheet(
  client: ApiClient,
  matchId: number,
  enrollmentId: number,
): Promise<TeamSheetRead> {
  const side: TeamSheetSide = { enrollment_id: enrollmentId }
  return client.post<TeamSheetRead>(
    `/v1/matches/${String(matchId)}/team-sheet/publish`,
    undefined,
    {
      query: side,
    },
  )
}

/** «Ci sono» or «non ci sono», for one convocato: theirs to say, or a tutore's. The last one said stands. */
export function answerCallUp(
  client: ApiClient,
  matchId: number,
  callUpId: number,
  answer: CallUpAnswer,
): Promise<CallUpAnsweredRead> {
  const body: CallUpAnswerPut = { answer }
  return client.put<CallUpAnsweredRead>(
    `/v1/matches/${String(matchId)}/team-sheet/call-ups/${String(callUpId)}/answer`,
    body,
  )
}

// Subscriptions. The server owns the price and the payer; the client only starts the
// hosted flows and renders the overview.

export type SubscriptionOverview = components['schemas']['SubscriptionOverview']
export type CoverageRead = components['schemas']['CoverageRead']
export type SubscriptionRead = components['schemas']['SubscriptionRead']
export type CheckoutRead = components['schemas']['CheckoutRead']
export type SetupRead = components['schemas']['SetupRead']
export type SubscriptionStatus = SubscriptionRead['status']

export function getSubscriptions(client: ApiClient): Promise<SubscriptionOverview> {
  return client.get<SubscriptionOverview>('/v1/subscriptions')
}

export function startCheckout(client: ApiClient, personId: number): Promise<CheckoutRead> {
  return client.post<CheckoutRead>('/v1/subscriptions', { person_id: personId })
}

export function cancelSubscription(
  client: ApiClient,
  subscriptionId: number,
): Promise<SubscriptionRead> {
  return client.post<SubscriptionRead>(`/v1/subscriptions/${String(subscriptionId)}/cancel`)
}

export function startCardChange(client: ApiClient, subscriptionId: number): Promise<SetupRead> {
  return client.post<SetupRead>(`/v1/subscriptions/${String(subscriptionId)}/payment-method`)
}
